import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, FileSpreadsheet, Check, X, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { cn, formatDateTime } from '@/lib/utils';

export function Import() {
  const queryClient = useQueryClient();
  const [dragActive, setDragActive] = useState(false);

  const { data: jobsData, isLoading } = useQuery({
    queryKey: ['import-jobs'],
    queryFn: () => api.getImportJobs(),
    refetchInterval: 5000, // Poll for updates
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.uploadFile(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['import-jobs'] });
    },
  });

  const jobs = jobsData?.jobs || [];

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      uploadMutation.mutate(e.dataTransfer.files[0]);
    }
  }, [uploadMutation]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      uploadMutation.mutate(e.target.files[0]);
    }
  }, [uploadMutation]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Import FCM Data</h2>
        <p className="text-sm text-muted-foreground">
          Upload CSV or Excel files from your FCM broker
        </p>
      </div>

      {/* Upload Zone */}
      <div
        className={cn(
          'rounded-lg border-2 border-dashed p-8 text-center transition-colors',
          dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25',
          uploadMutation.isPending && 'opacity-50 pointer-events-none'
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload"
          className="hidden"
          accept=".csv,.xlsx"
          onChange={handleFileInput}
          disabled={uploadMutation.isPending}
        />
        <label
          htmlFor="file-upload"
          className="cursor-pointer"
        >
          <div className="flex flex-col items-center gap-4">
            {uploadMutation.isPending ? (
              <Loader2 className="h-12 w-12 text-muted-foreground animate-spin" />
            ) : (
              <Upload className="h-12 w-12 text-muted-foreground" />
            )}
            <div>
              <p className="font-medium">
                {uploadMutation.isPending ? 'Uploading...' : 'Drop your file here or click to upload'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Supports CSV and Excel files up to 10MB
              </p>
            </div>
          </div>
        </label>
      </div>

      {uploadMutation.isError && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {uploadMutation.error instanceof Error ? uploadMutation.error.message : 'Upload failed'}
        </div>
      )}

      {/* Import Jobs */}
      <div className="rounded-lg border bg-card">
        <div className="border-b px-6 py-4">
          <h3 className="font-semibold">Recent Imports</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">File</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Uploaded</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-muted-foreground">Status</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-muted-foreground">Records</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-muted-foreground">Success</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-muted-foreground">Errors</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                    Loading import jobs...
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center">
                    <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No imports yet</p>
                  </td>
                </tr>
              ) : (
                jobs.map((job) => (
                  <tr key={job.id} className="border-b last:border-0">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{job.fileName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {formatDateTime(job.uploadedAt)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <StatusBadge status={job.status} />
                    </td>
                    <td className="px-6 py-4 text-right font-mono">
                      {job.totalRecords}
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-green-600">
                      {job.successCount}
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-red-600">
                      {job.errorCount > 0 ? job.errorCount : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { icon: typeof Check; className: string }> = {
    PENDING: { icon: Loader2, className: 'bg-yellow-100 text-yellow-700' },
    PROCESSING: { icon: Loader2, className: 'bg-blue-100 text-blue-700' },
    COMPLETED: { icon: Check, className: 'bg-green-100 text-green-700' },
    FAILED: { icon: X, className: 'bg-red-100 text-red-700' },
  };

  const { icon: Icon, className } = config[status] || config.PENDING;

  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium', className)}>
      <Icon className={cn('h-3 w-3', status === 'PROCESSING' && 'animate-spin')} />
      {status}
    </span>
  );
}
